import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FaUser, FaEnvelope, FaLock, FaGoogle } from 'react-icons/fa';
import toast from 'react-hot-toast';

export const LegalProfessionalSignUpForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [claimData, setClaimData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { signup, login } = useAuth();
  
  // Check for claim data on load
  useEffect(() => {
    const claimToken = localStorage.getItem('claimToken');
    const claimType = localStorage.getItem('claimType');
    const claimEmail = localStorage.getItem('claimEmail');
    
    // Get claim=true from URL query params
    const queryParams = new URLSearchParams(location.search);
    const isClaimFlow = queryParams.get('claim') === 'true';
    
    if (isClaimFlow && claimToken && claimType) {
      setClaimData({
        token: claimToken,
        type: claimType,
        email: claimEmail
      });
      
      // Pre-fill email if available
      if (claimEmail) {
        setFormData(prev => ({
          ...prev,
          email: claimEmail
        }));
      }
    }
  }, [location.search]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name || formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters.';
    }
    
    // Skip email validation if coming from claim link with email
    if (!claimData?.email && (!formData.email || !/\S+@\S+\.\S+/.test(formData.email))) {
      newErrors.email = 'Please enter a valid email address.';
    }
    
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters.';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords don\'t match.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Don't allow changing email if it's from a claim link
    if (name === 'email' && claimData?.email) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      // Additional data to send if this is a claim flow
      const additionalData = claimData ? {
        claimToken: claimData.token,
        claimType: claimData.type,
        autoVerify: true // Signal server to auto-verify this account
      } : {};
      
      // Make sure these details are still available for after login
      if (claimData && claimData.type) {
        localStorage.setItem('claimType', claimData.type);
      }
      
      // Pass 'legal_professional' as the role and any additional data
      const response = await signup(formData.name, formData.email, formData.password, 'legal_professional', additionalData);
      
      // Don't remove claimData yet, we'll need it after login
      
      // Customize success message based on claim flow
      const successMessage = claimData 
        ? 'Verified legal professional account created! You will now be signed in automatically.'
        : 'Legal Professional account created! You can now sign in with your credentials.';
      
      // Show success message with toast
      toast.success(successMessage, {
        duration: 5000,
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      
      // If it's a claim link, auto login and navigate to dashboard
      if (claimData) {
        try {
          await login(formData.email, formData.password);
          navigate('/dashboard');
        } catch (loginError) {
          console.error('Auto-login error:', loginError);
          navigate('/signin');
        }
      } else {
        navigate('/signin');
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrors({
        form: error.response?.data?.error || 'Something went wrong. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Add claim params to Google auth if in claim flow
    const baseUrl = `${process.env.REACT_APP_API_URL}/api/auth/google?role=legal_professional`;
    const claimParams = claimData 
      ? `&claimToken=${claimData.token}&claimType=${claimData.type}&autoVerify=true` 
      : '';
    
    window.location.href = baseUrl + claimParams;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {errors.form && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg">
          {errors.form}
        </div>
      )}
      
      {claimData && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 text-green-400 text-sm rounded-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>
              You're signing up from a claim link. Your account will be automatically verified and you'll see client details after signing in.
            </span>
          </div>
        </div>
      )}

      {/* Name field */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">Full Name</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaUser className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            name="name"
            placeholder="John Smith"
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
            value={formData.name}
            onChange={handleChange}
          />
        </div>
        {errors.name && (
          <p className="text-red-400 text-sm mt-1">{errors.name}</p>
        )}
      </div>

      {/* Email field */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">Email</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaEnvelope className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="email"
            name="email"
            placeholder="you@example.com"
            className={`w-full pl-10 pr-4 py-3 ${claimData?.email ? 'bg-white/10 text-gray-400' : 'bg-white/5'} border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all`}
            value={formData.email}
            onChange={handleChange}
            disabled={claimData?.email ? true : false}
            readOnly={claimData?.email ? true : false}
          />
        </div>
        {errors.email && (
          <p className="text-red-400 text-sm mt-1">{errors.email}</p>
        )}
        {claimData?.email && (
          <p className="text-green-400 text-xs mt-1">This email is linked to your claim and cannot be changed</p>
        )}
      </div>

      {/* Password field */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">Password</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaLock className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="password"
            name="password"
            placeholder="••••••••"
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
            value={formData.password}
            onChange={handleChange}
          />
        </div>
        {errors.password && (
          <p className="text-red-400 text-sm mt-1">{errors.password}</p>
        )}
      </div>

      {/* Confirm Password field */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">Confirm Password</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaLock className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="password"
            name="confirmPassword"
            placeholder="••••••••"
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
            value={formData.confirmPassword}
            onChange={handleChange}
          />
        </div>
        {errors.confirmPassword && (
          <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>
        )}
      </div>

      {/* Free subscription notice */}
      {claimData && (
        <div className="p-4 bg-purple-700/20 rounded-lg border border-purple-500/20">
          <h4 className="font-medium text-white mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Limited Time Offer
          </h4>
          <p className="text-gray-300 text-sm">
            Your subscription is currently set to £0 for a limited time. Create your account now to lock in this special rate!
          </p>
        </div>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Creating Account...
          </span>
        ) : claimData ? "Create Verified Account" : "Create Legal Professional Account"}
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-[#301555] text-gray-400">Or continue with</span>
        </div>
      </div>

      {/* Google signup */}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 font-medium rounded-lg transition-all"
      >
        <FaGoogle className="h-5 w-5" />
        Google
      </button>
    </form>
  );
}; 